import routes from 'sitewide-js/core_pre_webpack/routes';
import React, {
  useCallback,
  useState,
  KeyboardEvent,
  useEffect,
  MouseEvent,
  useRef,
} from 'react';
import styled from 'astroturf/react';
import { Paper } from 'js/social/shared/types';
import { Body } from 'js/design_system/Body';
import { Menu } from 'js/design_system/Menu';
import { SearchIcon } from 'js/social/shared/Icons';
import { usePaperSearch } from 'js/social/shared/attachments/usePaperSearch';
import { Spinner } from 'js/academia_components/transients/spinner';
import { PopoverWidthMode } from 'js/design_system/Popover';
import { mod } from 'js/social/shared/SocialEditor/utils';
import { scrollChildIntoView } from 'js/social/shared/utils';
import _Container from "PaperSelector-Container.module.scss!=!astroturf/inline-loader?style=1!/var/lib/jenkins-code/academia-app/app/assets/javascripts/social/shared/attachments/PaperSelector/PaperSelector.tsx?styleId=Container";
import _InputContainer from "PaperSelector-InputContainer.module.scss!=!astroturf/inline-loader?style=1!/var/lib/jenkins-code/academia-app/app/assets/javascripts/social/shared/attachments/PaperSelector/PaperSelector.tsx?styleId=InputContainer";
import _Link from "PaperSelector-Link.module.scss!=!astroturf/inline-loader?style=1!/var/lib/jenkins-code/academia-app/app/assets/javascripts/social/shared/attachments/PaperSelector/PaperSelector.tsx?styleId=Link";
import _Message from "PaperSelector-Message.module.scss!=!astroturf/inline-loader?style=1!/var/lib/jenkins-code/academia-app/app/assets/javascripts/social/shared/attachments/PaperSelector/PaperSelector.tsx?styleId=Message";
import _List from "PaperSelector-List.module.scss!=!astroturf/inline-loader?style=1!/var/lib/jenkins-code/academia-app/app/assets/javascripts/social/shared/attachments/PaperSelector/PaperSelector.tsx?styleId=List";
import _ListItem from "PaperSelector-ListItem.module.scss!=!astroturf/inline-loader?style=1!/var/lib/jenkins-code/academia-app/app/assets/javascripts/social/shared/attachments/PaperSelector/PaperSelector.tsx?styleId=ListItem";
import _PaperTitle from "PaperSelector-PaperTitle.module.scss!=!astroturf/inline-loader?style=1!/var/lib/jenkins-code/academia-app/app/assets/javascripts/social/shared/attachments/PaperSelector/PaperSelector.tsx?styleId=PaperTitle";
import _PaperAuthors from "PaperSelector-PaperAuthors.module.scss!=!astroturf/inline-loader?style=1!/var/lib/jenkins-code/academia-app/app/assets/javascripts/social/shared/attachments/PaperSelector/PaperSelector.tsx?styleId=PaperAuthors";


const Container = /*#__PURE__*/styled("div", null, {
  displayName: "Container",
  styles: _Container
});

const InputContainer = /*#__PURE__*/styled("div", null, {
  displayName: "InputContainer",
  styles: _InputContainer
});

const Link = /*#__PURE__*/styled("a", null, {
  displayName: "Link",
  styles: _Link
});

const Message = /*#__PURE__*/styled(Body, null, {
  displayName: "Message",
  styles: _Message
});

const List = /*#__PURE__*/styled("div", null, {
  displayName: "List",
  styles: _List
});

const ListItem = /*#__PURE__*/styled("div", null, {
  displayName: "ListItem",
  styles: _ListItem
});

const PaperTitle = /*#__PURE__*/styled(Body, null, {
  displayName: "PaperTitle",
  styles: _PaperTitle
});

const PaperAuthors = /*#__PURE__*/styled(Body, null, {
  displayName: "PaperAuthors",
  styles: _PaperAuthors
});

type Props = {
  onChange: (paper: Paper) => void;
};

export const PaperSelector: React.FC<Props> = ({ onChange }) => {
  const [showMenu, setShowMenu] = useState(false);
  const [search, setSearch] = useState('');
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [selectedDiv, setSelectedDiv] = useState<HTMLDivElement | null>(null);
  const popoverRef = useRef<HTMLDivElement>(null);
  const { papers, isLoading } = usePaperSearch(search);

  useEffect(() => {
    setSelectedIndex(0);
  }, [search]);

  useEffect(() => {
    scrollChildIntoView(popoverRef.current, selectedDiv);
  }, [selectedDiv]);

  const upload = (event: MouseEvent) => {
    if (
      !confirm(
        'You will lose the text you have already entered if you upload a new paper. Do you want to continue?',
      )
    ) {
      event.preventDefault();
    }
  };

  const handleKeyDown = useCallback(
    (event: KeyboardEvent) => {
      if (event.key === 'ArrowDown') {
        event.preventDefault();
        event.stopPropagation();
        setSelectedIndex(selectedIndex + 1);
      } else if (event.key === 'ArrowUp') {
        event.preventDefault();
        event.stopPropagation();
        setSelectedIndex(selectedIndex - 1);
      } else if (
        (event.key === 'Enter' || event.key === 'Tab') &&
        papers.length > 0
      ) {
        event.preventDefault();
        event.stopPropagation();
        const index = mod(selectedIndex, papers.length);
        onChange(papers[index]);
      }
    },
    [papers, selectedIndex, onChange],
  );

  const renderMenu = useCallback(() => {
    if (isLoading) {
      return <Spinner show />;
    } else if (papers.length <= 0) {
      return <Message size="md">No results found.</Message>;
    } else {
      return (
        <List>
          {papers.map((paper, index) => {
            const selected = mod(selectedIndex, papers.length) === index;
            return (
              <ListItem
                key={paper.id}
                ref={(ref) => selected && setSelectedDiv(ref)}
                selected={selected}
                onMouseEnter={() => setSelectedIndex(index)}
                onClick={() => onChange(paper)}
              >
                <PaperTitle>{paper.title}</PaperTitle>
                <PaperAuthors>
                  {paper.authors.map((p) => p.displayName).join(', ')}
                </PaperAuthors>
              </ListItem>
            );
          })}
        </List>
      );
    }
  }, [papers, isLoading, selectedIndex, onChange]);

  return (
    <Container>
      <Menu
        popoverRef={popoverRef}
        open={showMenu}
        widthMode={PopoverWidthMode.Activator}
        maxHeight={256}
        render={renderMenu}
        onChange={setShowMenu}
      >
        <InputContainer>
          <SearchIcon color="#91919E" />

          <input
            placeholder="Search for papers on Academia.edu"
            autoFocus
            value={search}
            onFocus={() => setShowMenu(true)}
            onKeyDown={handleKeyDown}
            onChange={(event) => setSearch(event.target.value)}
          />
        </InputContainer>
      </Menu>

      <Body size="md">
        Or,{' '}
        <Link href={routes.upload_funnel_url()} onClick={upload}>
          upload a paper
        </Link>{' '}
        to Academia.edu
      </Body>
    </Container>
  );
};
